import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme, type Theme } from '../contexts/ThemeContext.js';
import { api } from '../services/api.js';

export interface FamilyMember {
  label: string | null;
  age: number;
}

export interface ProfileData {
  name: string;
  email: string;
  role: string;
  familySize: number;
  budget: number | null;
  dietaryRestrictions: string[];
  cuisinePreferences: string[];
  zipCode: string | null;
  onboardingComplete: boolean;
  theme: Theme;
  tier: string;
  expiresAt: string | null;
  // Phase B
  equipment: string[];
  skillLevel: string;
  busyNightMode: boolean;
  // Phase D
  familyMembers: FamilyMember[] | null;
  // Phase E
  hiddenVeggieMode: boolean;
  pickyEaterMode: boolean;
}

// Fetch the current user's profile to determine auth state.
// If 401 is returned, the API client redirects to /login.
// A successful response means the user is authenticated.
export function useProfile() {
  const { setTheme } = useTheme();

  return useQuery<ProfileData>({
    queryKey: ['profile'],
    queryFn: async () => {
      const data = await api.get<ProfileData>('/api/profile');
      // Sync server-stored theme to the local ThemeContext
      if (data.theme) setTheme(data.theme as Theme);
      return data;
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Silent auth check for public pages (landing, login, signup).
 * Returns the profile if logged in, null if not — never redirects to /login.
 */
export function useAuthStatus() {
  const { setTheme } = useTheme();
  return useQuery<ProfileData | null>({
    queryKey: ['auth-status'],
    queryFn: async () => {
      try {
        const data = await api.get<ProfileData>('/api/profile', { silent: true });
        if (data?.theme) setTheme(data.theme as Theme);
        return data;
      } catch {
        return null; // unauthenticated — stay on the public page
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      qc.clear();
      window.location.href = '/login';
    },
  });
}

export function useLogoutAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout-all'),
    onSuccess: () => {
      qc.clear();
      window.location.href = '/login';
    },
  });
}

/** Returns days remaining in trial (0 if expired/not trial). */
export function useTrialDaysLeft(profile: ProfileData | undefined): number | null {
  if (!profile || profile.tier !== 'trial' || !profile.expiresAt) return null;
  const ms = new Date(profile.expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}
